import React, { useState, useEffect, useRef } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { NOTIFICATION_POLL_INTERVAL_MS } from '../constants/notifications';
import { useAuth } from '../context/AuthContext';
import { notificationApi } from '../services/api';
import { notificationActionPath } from '../utils/notificationLinks';
import { localizeNotification } from '../utils/notificationText';
import { announceNewNotifications } from '../hooks/useRefreshOnNotification';

interface Props {
  notificationsPath?: string;
}

export function NotificationBell({ notificationsPath = '/notifications' }: Props) {
  const { t } = useTranslation();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [notifications, setNotifications] = useState<any[]>([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [showDropdown, setShowDropdown] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  // Ids seen so far; null until the first load, which announces nothing.
  const seenIds = useRef<Set<string> | null>(null);

  useEffect(() => {
    const load = async () => {
      try {
        const [notifs, countData] = await Promise.all([
          notificationApi.getMyNotifications(),
          notificationApi.getUnreadCount()
        ]);
        setNotifications(notifs.slice(0, 10));
        setUnreadCount(countData.count);
        // Something happened elsewhere (e.g. the customer sent a contract
        // back): let the open page refresh its figures without a reload.
        const fresh = notifs.filter((item: { id: string }) => seenIds.current && !seenIds.current.has(item.id));
        seenIds.current = new Set(notifs.map((item: { id: string }) => item.id));
        if (fresh.length > 0) announceNewNotifications(fresh.map((item: { eventKey?: string }) => item.eventKey ?? ''));
      } catch {}
    };
    load();
    const interval = setInterval(load, NOTIFICATION_POLL_INTERVAL_MS);
    return () => clearInterval(interval);
  }, []);

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) {
        setShowDropdown(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleMarkAsRead = async (id: string) => {
    try {
      await notificationApi.markAsRead(id);
      setNotifications(prev => prev.map(n => n.id === id ? { ...n, read: true } : n));
      setUnreadCount(prev => Math.max(0, prev - 1));
    } catch {}
  };

  const handleMarkAllAsRead = async () => {
    try {
      await notificationApi.markAllAsRead();
      setNotifications(prev => prev.map(n => ({ ...n, read: true })));
      setUnreadCount(0);
    } catch {}
  };

  return (
    <div className="nxl-h-item" ref={ref} style={{ position: 'relative' }}>
      <button
        type="button"
        className="nxl-head-link"
        onClick={(e) => { e.preventDefault(); setShowDropdown(prev => !prev); }}
      >
        <i className="feather-bell"></i>
        {unreadCount > 0 && (
          <span className="badge bg-danger nxl-h-badge">{unreadCount > 9 ? '9+' : unreadCount}</span>
        )}
      </button>
      {showDropdown && (
        <div
          className="dropdown-menu show shadow-lg"
          style={{
            position: 'absolute',
            right: 0,
            top: '100%',
            width: '340px',
            maxHeight: '420px',
            overflow: 'hidden',
            display: 'flex',
            flexDirection: 'column',
            zIndex: 1100
          }}
        >
          <div className="d-flex justify-content-between align-items-center px-3 py-2 border-bottom">
            <h6 className="mb-0 fw-bold">{t('nav.notifications')}</h6>
            {unreadCount > 0 && (
              <button className="btn btn-link btn-sm p-0 text-decoration-none" onClick={handleMarkAllAsRead}>
                {t('notifications.markAllRead')}
              </button>
            )}
          </div>
          <div style={{ overflowY: 'auto', flex: 1 }}>
            {notifications.length === 0 ? (
              <div className="text-center py-4 text-muted">
                <i className="feather-bell-off d-block mb-2"></i>
                <small>{t('notifications.noNotifications')}</small>
              </div>
            ) : (
              notifications.map(n => {
                const actionPath = notificationActionPath(n, user);
                const { title, body } = localizeNotification(n, t);
                return (
                  <div
                    key={n.id}
                    className={`px-3 py-2 border-bottom ${!n.read ? 'bg-light' : ''}`}
                    style={{ cursor: actionPath ? 'pointer' : 'default' }}
                    onClick={() => {
                      handleMarkAsRead(n.id);
                      setShowDropdown(false);
                      if (actionPath) navigate(actionPath);
                    }}
                  >
                  <div className="d-flex align-items-start gap-2">
                    <div className="flex-shrink-0 mt-1">
                      <span className={`badge bg-${n.metadata?.color || 'secondary'} rounded-circle p-1`}>
                        <i className={`feather-${n.metadata?.icon || 'bell'}`} style={{ fontSize: '12px' }}></i>
                      </span>
                    </div>
                    <div className="flex-grow-1" style={{ minWidth: 0 }}>
                      <div className={`small ${!n.read ? 'fw-bold' : ''}`} style={{ overflowWrap: 'anywhere' }}>{title}</div>
                      {/* At most four lines, so one long objection does not fill the menu. */}
                      <div className="text-muted notification-bell-body" style={{ fontSize: '0.75rem', overflowWrap: 'anywhere' }} title={body}>{body}</div>
                      <div className="text-muted" style={{ fontSize: '0.7rem' }}>
                        {new Date(n.createdAt).toLocaleString('sv-SE', { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' })}
                      </div>
                    </div>
                    {!n.read && (
                      <span className="flex-shrink-0 bg-primary rounded-circle" style={{ width: 8, height: 8, marginTop: 6 }}></span>
                    )}
                  </div>
                  </div>
                );
              })
            )}
          </div>
          <Link
            to={notificationsPath}
            className="d-block text-center py-2 border-top text-decoration-none small fw-semibold"
            onClick={() => setShowDropdown(false)}
          >
            {t('notifications.viewAll')}
          </Link>
        </div>
      )}
    </div>
  );
}
